import { useEffect, useMemo, useRef, useState } from "react";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";
import { apiGet, apiSend } from "../api";

// 模板管理:上传/批量上传/启停/删除/改分类。列表按分类分组显示,顶部
// 一个"上传"按钮。批量上传弹窗允许一次拖多张,共享同一 (category, subcategory)。

interface Template {
  id: string; name: string; category: string; subcategory: string; subsubcategory: string;
  image: string; enabled: boolean; usedCount: number; prompt: string;
}
interface CategoryNode { id: string; name: string; children?: CategoryNode[] }

export default function Templates() {
  const [list, setList] = useState<Template[]>([]);
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  const load = async () => {
    const [tpls, cats] = await Promise.all([
      apiGet<Template[]>("/templates"),
      apiGet<CategoryNode[]>("/categories"),
    ]);
    setList(tpls); setTree(cats);
  };
  useEffect(() => { load().catch((e) => setErr(String(e))); }, []);

  const toggle = async (t: Template) => {
    try { await apiSend("POST", `/templates/${t.id}/toggle`); await load(); }
    catch (e) { setErr(String(e)); }
  };
  const remove = async (t: Template) => {
    if (!confirm(`确认删除模板「${t.name}」?已生成的记录仍会保留其快照。`)) return;
    try { await apiSend("DELETE", `/templates/${t.id}`); await load(); }
    catch (e) { setErr(String(e)); }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, Template[]>();
    for (const t of list) {
      const k = `${t.category || "(未分类)"}${t.subcategory ? " · " + t.subcategory : ""}${t.subsubcategory ? " · " + t.subsubcategory : ""}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return Array.from(m.entries());
  }, [list]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800">模板管理</h1>
          <p className="text-xs text-slate-500 mt-0.5">共 {list.length} 张,启用 {list.filter((t) => t.enabled).length} 张</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowBatch(true)} className="flex-1 sm:flex-none px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50">批量上传</button>
          <button onClick={() => setShowUpload(true)} className="flex-1 sm:flex-none px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700">+ 新增模板</button>
        </div>
      </div>
      {err && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}

      {list.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
          还没有模板,点右上「新增模板」上传第一张
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([groupName, items]) => (
            <div key={groupName}>
              <div className="text-sm font-medium text-slate-600 mb-2">{groupName} <span className="text-xs text-slate-400 ml-1">{items.length} 张</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                {items.map((t) => (
                  <div key={t.id} className="bg-white rounded-lg border border-slate-200 p-2 space-y-2">
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`查看模板图: ${t.name}`}
                      onClick={() => setPreviewTemplate(t)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setPreviewTemplate(t);
                        }
                      }}
                      className="aspect-[4/5] bg-slate-100 rounded overflow-hidden relative cursor-pointer group focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]" />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/45 to-transparent px-2 pb-2 pt-8 opacity-0 transition-opacity group-hover:opacity-100">
                        <span className="text-[10px] text-white">点击放大</span>
                      </div>
                      {!t.enabled && (
                        <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center text-white text-xs">已停用</div>
                      )}
                    </div>
                    <div className="text-xs text-slate-700 line-clamp-1">{t.name}</div>
                    <div className="text-[10px] text-slate-400">用过 {t.usedCount} 次</div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggle(t)} className="flex-1 px-2 py-1 text-xs rounded bg-slate-100 hover:bg-slate-200">
                        {t.enabled ? "停用" : "启用"}
                      </button>
                      <button onClick={() => remove(t)} className="flex-1 px-2 py-1 text-xs rounded bg-rose-50 text-rose-600 hover:bg-rose-100">
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && <UploadModal tree={tree} onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {showBatch && <BatchModal tree={tree} onClose={() => setShowBatch(false)} onDone={() => { setShowBatch(false); load(); }} />}
      {previewTemplate && <TemplatePreviewModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />}
    </div>
  );
}

// ─── Upload single template ─────────────────────────────────────────
function UploadModal({ tree, onClose, onDone }: { tree: CategoryNode[]; onClose: () => void; onDone: () => void }) {
  const topOptions = tree;
  const [category, setCategory] = useState(topOptions[0]?.name || "");
  const [subcategory, setSubcategory] = useState("");
  const [subsubcategory, setSubsubcategory] = useState("");
  const subOptions = topOptions.find((t) => t.name === category)?.children || [];
  const sub2Options = subOptions.find((s) => s.name === subcategory)?.children || [];
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const preview = file ? URL.createObjectURL(file) : "";
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const submit = async () => {
    if (!file) { setErr("请选择模板图片"); return; }
    if (!name.trim()) { setErr("请填写名称"); return; }
    if (!category) { setErr("请先选择分类"); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("category", category);
      fd.append("subcategory", subcategory || "");
      fd.append("subsubcategory", subsubcategory || "");
      fd.append("prompt", prompt.trim());
      fd.append("file", file);
      await apiSend("POST", "/templates", fd);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal title="新增模板" onClose={onClose}>
      <div className="space-y-3">
        <div onClick={() => fileRef.current?.click()} className="aspect-[4/5] max-h-64 mx-auto bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer flex items-center justify-center overflow-hidden">
          {preview ? <img src={preview} className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400">点击选择模板图</span>}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        <Field label="名称"><input value={name} onChange={(e) => setName(e.target.value)} className="input" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Field label="一级分类">
            <select value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(""); setSubsubcategory(""); }} className="input">
              {topOptions.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="二级分类">
            <select value={subcategory} onChange={(e) => { setSubcategory(e.target.value); setSubsubcategory(""); }} className="input" disabled={subOptions.length === 0}>
              <option value="">(无)</option>
              {subOptions.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="三级分类">
            <select value={subsubcategory} onChange={(e) => setSubsubcategory(e.target.value)} className="input" disabled={sub2Options.length === 0}>
              <option value="">(无)</option>
              {sub2Options.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="提示词 (可选)">
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} className="input resize-none" />
        </Field>
        {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded">取消</button>
          <button onClick={submit} disabled={busy} className="flex-1 px-3 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-60">{busy ? "上传中…" : "创建"}</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Batch upload ─────────────────────────────────────────────────
function BatchModal({ tree, onClose, onDone }: { tree: CategoryNode[]; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState(tree[0]?.name || "");
  const [subcategory, setSubcategory] = useState("");
  const [subsubcategory, setSubsubcategory] = useState("");
  const subOptions = tree.find((t) => t.name === category)?.children || [];
  const sub2Options = subOptions.find((s) => s.name === subcategory)?.children || [];
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (files.length === 0) { setErr("请选择至少一张"); return; }
    if (!category) { setErr("请先选择一级分类"); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("subcategory", subcategory || "");
      fd.append("subsubcategory", subsubcategory || "");
      for (const f of files) fd.append("files", f);
      await apiSend("POST", "/templates/batch", fd);
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal title="批量上传模板" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Field label="一级分类">
            <select value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(""); setSubsubcategory(""); }} className="input">
              {tree.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="二级分类">
            <select value={subcategory} onChange={(e) => { setSubcategory(e.target.value); setSubsubcategory(""); }} className="input" disabled={subOptions.length === 0}>
              <option value="">(无)</option>
              {subOptions.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="三级分类">
            <select value={subsubcategory} onChange={(e) => setSubsubcategory(e.target.value)} className="input" disabled={sub2Options.length === 0}>
              <option value="">(无)</option>
              {sub2Options.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="模板图片(多选)">
          <input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} className="text-xs" />
        </Field>
        {files.length > 0 && <div className="text-xs text-slate-500">已选 {files.length} 张 · 名称将取自文件名</div>}
        {err && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded">取消</button>
          <button onClick={submit} disabled={busy || files.length === 0} className="flex-1 px-3 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-60">{busy ? "上传中…" : `上传 ${files.length} 张`}</button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Building blocks ────────────────────────────────────────────
function TemplatePreviewModal({ template, onClose }: { template: Template; onClose: () => void }) {
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null);
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [currentScale, setCurrentScale] = useState(1);
  const fitScale = useMemo(() => {
    if (!imageSize) return 1;
    return Math.min(1, (viewport.w * 0.98) / imageSize.w, (viewport.h * 0.94) / imageSize.h);
  }, [imageSize, viewport]);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (imageSize) setCurrentScale(fitScale);
  }, [imageSize]);

  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const category = [template.category, template.subcategory, template.subsubcategory].filter(Boolean).join(" · ") || "未分类";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-slate-950/90 p-2 backdrop-blur-sm sm:p-5"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label={`查看模板图: ${template.name}`}
    >
      <div className="relative h-[94vh] w-[99vw] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <TransformWrapper
          key={template.id}
          initialScale={fitScale}
          minScale={Math.min(fitScale, 0.35)}
          maxScale={5}
          centerOnInit
          limitToBounds={false}
          wheel={{ step: 0.12 }}
          pinch={{ step: 5 }}
          panning={{ velocityDisabled: true }}
          doubleClick={{ mode: "zoomIn", step: 0.7 }}
          onTransformed={(_, state) => setCurrentScale(state.scale)}
        >
          {({ zoomIn, zoomOut, centerView }) => (
            <>
              {imageSize && <PreviewAutoCenter fitScale={fitScale} />}
              <TransformComponent
                wrapperClass="!h-full !w-full"
                contentClass="!h-full !w-full cursor-grab active:cursor-grabbing"
              >
                <div className="flex h-full w-full items-center justify-center">
                  <img
                    src={template.image}
                    alt={template.name}
                    draggable={false}
                    onLoad={(e) => setImageSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                    className="max-h-none max-w-none select-none"
                  />
                </div>
              </TransformComponent>

              <div className="pointer-events-none absolute inset-x-3 top-3 flex items-start justify-between gap-3 sm:inset-x-5 sm:top-5">
                <div className="pointer-events-auto min-w-0 rounded-2xl bg-black/35 px-3 py-2 text-white shadow-lg ring-1 ring-white/15 backdrop-blur-md sm:px-4">
                  <div className="truncate text-sm font-medium sm:text-base">{template.name}</div>
                  <div className="mt-0.5 truncate text-[11px] text-white/65 sm:text-xs">
                    {category} · {template.enabled ? "已启用" : "已停用"} · 用过 {template.usedCount} 次
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="pointer-events-auto grid h-10 w-10 shrink-0 place-items-center rounded-full bg-black/35 text-xl text-white shadow-lg ring-1 ring-white/15 backdrop-blur-md hover:bg-black/50"
                  aria-label="关闭预览"
                >
                  ×
                </button>
              </div>

              <div className="pointer-events-auto absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/35 p-1 text-xs text-white shadow-lg ring-1 ring-white/15 backdrop-blur-md sm:bottom-5">
                <button onClick={() => zoomOut()} className="rounded-full px-3 py-2 hover:bg-white/15" aria-label="缩小">−</button>
                <button onClick={() => zoomIn()} className="rounded-full px-3 py-2 hover:bg-white/15" aria-label="放大">＋</button>
                <button onClick={() => centerView(fitScale, 0)} className="rounded-full px-3 py-2 hover:bg-white/15">重置</button>
                <button onClick={() => centerView()} className="hidden rounded-full px-3 py-2 hover:bg-white/15 sm:block">居中</button>
                <span className="hidden min-w-10 px-2 py-2 text-center text-white/70 sm:inline">{Math.round(currentScale * 100)}%</span>
                <span className="hidden border-l border-white/20 px-3 py-2 text-white/60 sm:inline">滚轮 / 双指缩放 · 拖动查看</span>
              </div>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>
  );
}

function PreviewAutoCenter({ fitScale }: { fitScale: number }) {
  const { centerView } = useControls();
  const centeredRef = useRef(false);

  useEffect(() => {
    if (centeredRef.current) return;
    centeredRef.current = true;
    centerView(fitScale, 0);
  }, [centerView, fitScale]);

  return null;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md mx-4 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-base font-semibold text-slate-800">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
